/**
 * PII scrubbing for the shared PostHog instance's `before_send` hook
 * (site-wide exception capture — plan §5).
 *
 * Mirrors the masking policy in
 * fct-commerce-services/src/portal/auth-observability.ts: mask emails to
 * `j***@domain`, let Salesforce/Shopify IDs through in full. Duplicated
 * rather than shared — a cross-repo package for this much logic would be a
 * dependency, a version, and a release process. Keep the two in sync by
 * hand; each copy carries this comment pointing at the other.
 *
 * Touches only the properties named in plan §5 — the URL-shaped ones, the
 * exception text, and the campaign params posthog-js hoists out of the URL.
 * Everything else — including PROTECTED_KEYS — passes through untouched. See
 * posthog-js #3438: a scrubber that strips `properties.token` silently 401s
 * every subsequent event, which is why these lists are explicit rather than
 * pattern-matched.
 */

const EMAIL_PATTERN = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

// Fireclay order references, e.g. "FC-123456".
const FC_NUMBER_PATTERN = /\bFC-\d{4,}\b/gi;

// Query params allowed to survive on $current_url / $referrer. Deliberately
// narrow: order-status (`?key=`) and portal (`?token=`) routes carry real
// identifiers in query strings, so this is an allowlist, not a denylist.
const ALLOWED_QUERY_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

// URL-shaped properties. `$session_entry_*` are posthog-js's copies of the
// first URL of the session and are NOT derived from the scrubbed
// `$current_url` — they are captured separately and reach the wire raw unless
// listed here (plan §6 payload check, 2026-08-18).
const URL_PROPERTIES = ['$current_url', '$referrer', '$session_entry_url', '$session_entry_referrer'];

// posthog-js's stand-in when there is no `document.referrer`. It is the one
// non-URL value expected in a URL property, so it survives verbatim.
const NO_REFERRER = '$direct';

// Anything else that will not parse as a URL is replaced rather than passed
// through: the allowlist and fragment/credential clearing below can only run
// on a parsed URL, so a malformed value would otherwise reach the wire with
// whatever query string or fragment it carried. In practice nothing lands here
// — these properties come from `location.href` and `document.referrer` — so a
// marker loses nothing and keeps the unparseable case from being the one hole
// in the policy.
const UNPARSEABLE_URL = '[unparseable url]';

// posthog-js also lifts campaign params *out* of the URL into standalone
// properties, plus a `$session_entry_` copy of each. Those bypass scrubUrl
// entirely, which is how a raw email pasted into `utm_content` reached the
// wire even with the URL allowlist masking its copy inside `$current_url`.
// Masked rather than dropped: click IDs are identifiers, which this policy
// has always let through, and the mask only bites on emails/FC numbers.
const CAMPAIGN_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'gad_source',
    'gclsrc',
    'dclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'msclkid',
    'twclid',
    'li_fat_id',
    'mc_cid',
    'igshid',
    'ttclid',
    'rdt_cid',
    'epik',
    'qclid',
    'sccid',
    'irclid',
    '_kx',
];

// Both the bare param and posthog-js's session-entry copy of it.
const MASKED_PROPERTIES = [
    'message',
    ...CAMPAIGN_PARAMS,
    ...CAMPAIGN_PARAMS.map((param) => `$session_entry_${param}`),
];

// Never stripped, whatever the PII patterns above would otherwise match.
export const PROTECTED_KEYS = [
    'token',
    'distinct_id',
    '$session_id',
    '$window_id',
    '$lib',
    '$lib_version',
    '$insert_id',
    '$device_id',
    '$time',
];

function maskEmail(match) {
    const at = match.indexOf('@');
    if (at <= 0) return match;
    return `${match[0]}***${match.slice(at)}`;
}

function maskText(value) {
    if (typeof value !== 'string') return value;
    return value.replace(EMAIL_PATTERN, maskEmail).replace(FC_NUMBER_PATTERN, 'FC-***');
}

function scrubUrl(url) {
    if (typeof url !== 'string' || url === '') return url;
    if (url === NO_REFERRER) return url;
    let parsed;
    try {
        parsed = new URL(url);
    } catch {
        return UNPARSEABLE_URL;
    }
    // Credentials and the fragment carry PII as readily as the query string
    // (`https://user:pass@host/`, `#email=...`) and neither is covered by the
    // allowlist below, so both are cleared outright.
    parsed.username = '';
    parsed.password = '';
    parsed.hash = '';
    const kept = new URLSearchParams();
    for (const [key, value] of parsed.searchParams) {
        // The allowlist protects the param *name*; the value is still
        // caller-supplied (campaign builders paste emails into utm_content),
        // so retained values get the same masking as message text.
        if (ALLOWED_QUERY_PARAMS.includes(key)) kept.append(key, maskText(value));
    }
    parsed.search = kept.toString();
    return parsed.toString();
}

function scrubExceptionList(list) {
    if (!Array.isArray(list)) return list;
    return list.map((entry) =>
        entry && typeof entry.value === 'string' ? { ...entry, value: maskText(entry.value) } : entry,
    );
}

/**
 * Returns a copy of `event` with PII scrubbed from its properties. Callers
 * (createPosthogBeforeSend.js) are responsible for only invoking this on
 * `$exception` events.
 *
 * @param {object} event A PostHog capture event (`{ event, properties }`).
 * @returns {object} A copy of `event` with PII scrubbed from its properties.
 */
export function scrubPii(event) {
    if (!event || typeof event !== 'object') return event;
    const { properties } = event;
    if (!properties || typeof properties !== 'object') return event;

    const scrubbed = { ...properties };
    for (const key of URL_PROPERTIES) {
        if (key in scrubbed) scrubbed[key] = scrubUrl(scrubbed[key]);
    }
    for (const key of MASKED_PROPERTIES) {
        if (key in scrubbed) scrubbed[key] = maskText(scrubbed[key]);
    }
    if ('$exception_list' in scrubbed) scrubbed.$exception_list = scrubExceptionList(scrubbed.$exception_list);

    return { ...event, properties: scrubbed };
}
